// Pure projection for the Project drawer Financials tab (Task #9168).
// Category rows remain an independent breakdown. Record values own headlines;
// only complete absence of record values permits a labeled category fallback.
// Values are converted ONCE per row/actuals measure into the
// display currency (existing convertAmount/useFxRates conventions) and kept
// unrounded; rounding happens only in formatCompact at render time.
// Unknown (null) is never coerced to zero. TIA is excluded from every total
// and the bar. Other is included in the Grand total only, exactly once.
import { convertAmount } from "../../../utils/fxConvert.js";
import { getCurrencySymbol } from "../../../utils/format.js";

export const FIN_FIELDS = ["budget", "committed", "forecast", "invoiced"];
export const FIN_LABELS = { soft: "Soft costs", hard: "Hard costs", ffe: "FF&E", contingency: "Contingency", other: "Other", tia: "TIA" };
const COST = ["soft", "hard", "ffe"];

export const normCcy = (v) => (v == null ? "" : String(v).trim().toUpperCase());
const isBlank = (v) => v == null || (typeof v === "string" && !v.trim());
const num = (v) => { if (isBlank(v) || !["number", "string"].includes(typeof v)) return null; const n = Number(v); return Number.isFinite(n) ? n : null; };
const populated = (row) => FIN_FIELDS.some((k) => num(row[k]) != null);

export function sumKnown(values) {
  const known = values.filter((v) => v != null);
  return known.length ? known.reduce((a, b) => a + b, 0) : null;
}
export const diff = (a, b) => (a != null && b != null ? a - b : null);
export const ratio = (n, d) => (n != null && d != null && d > 0 ? n / d : null);

/** Display currency: the project's currency, else the single row currency in use. */
export function resolveDisplayCurrency(project, budgets) {
  // The CapEx project-record fields are USD regardless of currency metadata.
  // Native category rows still convert from their own currency, exactly once.
  if (hasRecordFinancials(project) || project?.financialsTracked === false) return "USD";
  const own = normCcy(project?.currency);
  if (own) return own;
  const set = [...new Set((budgets || []).filter((r) => r.category !== "tia").map((r) => normCcy(r.currency)).filter(Boolean))];
  return set.length === 1 ? set[0] : "";
}

/**
 * Category-dataset source statement. The project record's CapEx provenance
 * describes the record summary fields, not these rows, so it is NOT used here.
 */
export const CATEGORY_SOURCE_TEXT = "Source: this project's category budget rows as recorded in Places, each in its own source currency. No structured import or document provenance is provided; row notes, where present, are shown as entered.";

function projectRow(row, display, rates) {
  const currency = normCcy(row.currency);
  const native = Object.fromEntries(FIN_FIELDS.map((k) => [k, num(row[k])]));
  let issue = null;
  if (populated(row) && !currency) issue = "missing-currency";
  else if (populated(row) && !display) issue = "missing-display";
  const values = {};
  for (const k of FIN_FIELDS) {
    if (native[k] == null) { values[k] = null; continue; }
    const c = issue ? null : convertAmount(native[k], currency, display, rates);
    if (c == null && !issue) issue = "missing-rate";
    values[k] = c;
  }
  if (issue) for (const k of FIN_FIELDS) values[k] = null;
  return {
    id: row.id, category: row.category, label: FIN_LABELS[row.category] || row.category,
    currency, native, issue, converted: !issue && currency !== display,
    ...values,
    variance: diff(values.budget, values.forecast),
    ratio: ratio(values.forecast, values.budget),
    costRsf: num(row.costRsf), note: row.note ? String(row.note) : "",
    raw: row,
  };
}

function totalOf(rows) {
  const t = Object.fromEntries(FIN_FIELDS.map((k) => [k, sumKnown(rows.map((r) => r[k]))]));
  return { ...t, variance: diff(t.budget, t.forecast), ratio: ratio(t.forecast, t.budget) };
}

const EMPTY_TOTAL = { budget: null, committed: null, forecast: null, invoiced: null, variance: null, ratio: null };

export function projectDrawerFinancials(budgets, { displayCurrency, rates, actuals, project, categoriesReady = true } = {}) {
  const list = Array.isArray(budgets) ? budgets : [];
  const display = normCcy(displayCurrency);
  const all = list.map((r) => projectRow(r, display, rates));
  const byCat = (c) => all.filter((r) => r.category === c);
  const costRows = all.filter((r) => COST.includes(r.category)).sort((a, b) => COST.indexOf(a.category) - COST.indexOf(b.category));
  const contingency = byCat("contingency");
  const other = byCat("other");
  const tia = byCat("tia");
  const totalRows = [...costRows, ...contingency, ...other];
  const issues = [...new Set(totalRows.map((r) => r.issue).filter(Boolean))];
  const totalsAvailable = issues.length === 0;

  let subtotal = EMPTY_TOTAL; let grandTotal = EMPTY_TOTAL;
  if (totalsAvailable) {
    subtotal = totalOf(costRows);
    // Existing rule: Grand total exists only when the subtotal is known;
    // Contingency and Other add their known values, each exactly once.
    const extras = [...contingency, ...other];
    const g = Object.fromEntries(FIN_FIELDS.map((k) => [k, subtotal[k] == null ? null
      : subtotal[k] + (sumKnown(extras.map((r) => r[k])) ?? 0)]));
    grandTotal = { ...g, variance: diff(g.budget, g.forecast), ratio: ratio(g.forecast, g.budget) };
  }

  const spent = project ? recordActuals(project, actuals, display, rates) : projectActuals(actuals, display, rates);
  const recordBasis = hasRecordFinancials(project);
  const untracked = project?.financialsTracked === false;
  const recordValue = key => convertAmount(num(project?.[key]), "USD", display, rates);
  const categoryFallback = !recordBasis && !untracked;
  const budget = untracked ? null : recordBasis ? recordValue("approvedBudget") : categoriesReady ? grandTotal.budget : null;
  const forecast = untracked ? null : recordBasis ? recordValue("forecastAtCompletion") : categoriesReady ? grandTotal.forecast : null;
  const summary = {
    budget,
    forecast,
    variance: diff(budget, forecast),
    invoiced: grandTotal.invoiced,
    invoicedPctOfForecast: ratio(grandTotal.invoiced, grandTotal.forecast),
    spent: untracked ? null : spent.value,
    remaining: untracked ? null : diff(forecast, spent.value),
    remainingBasis: "actuals",
    spentPctOfForecast: untracked ? null : ratio(spent.value, forecast),
  };

  const contingencyForecast = contingency.length ? sumKnown(contingency.map((r) => r.forecast)) : 0;
  const bar = recordBasis || untracked
    ? buildHeadlineBar(summary, spent.reason)
    : buildBar({ totalsAvailable: totalsAvailable && categoriesReady, forecast, spent: spent.value, actualsReason: spent.reason, contingencyForecast, contingencyRowUnknown: contingency.length > 0 && contingencyForecast == null });
  const discrepancies = !categoryFallback && !untracked && totalsAvailable && categoriesReady
    ? ["budget", "forecast", "variance"].filter(key => summary[key] != null && grandTotal[key] != null && Math.abs(summary[key] - grandTotal[key]) >= SUB_CENT)
      .map(key => ({ key, category: grandTotal[key], headline: summary[key] }))
    : [];
  const headlineIssues = {
    budget: recordBasis ? "Project-record Budget is missing, invalid or cannot be converted safely. Partial records do not fall back to category totals." : "Category Budget is not yet available.",
    forecast: recordBasis ? "Project-record Forecast is missing, invalid or cannot be converted safely. Partial records do not fall back to category totals." : "Category Forecast is not yet available.",
    variance: "Variance requires known Budget and Forecast on the headline basis.",
    spent: spent.reason || "No actuals recorded.",
    remaining: "Remaining requires known Forecast and valid project-record actuals; category invoices are never substituted.",
  };

  return {
    displayCurrency: display,
    status: list.length === 0 ? "empty" : "ok",
    rows: costRows, contingency, other, tia,
    subtotal, grandTotal, summary, bar, actuals: spent, discrepancies, headlineIssues,
    headlineBasis: untracked ? "Not financially tracked" : recordBasis ? "Project-record / CapEx headlines · USD, ex tax" : "Category-total headline fallback",
    categoryFallback, untracked,
    totalsAvailable, issues,
    showCommitted: totalRows.some((r) => r.native.committed != null),
    anyConverted: all.some((r) => r.converted) || (spent.status === "ok" && spent.currency !== display),
  };
}

export function hasRecordFinancials(project) {
  return ["approvedBudget", "forecastAtCompletion", "spentToDate"].some(key => !isBlank(project?.[key]));
}

// A known record amount is still readable without provenance. Do not invent
// source/cutoff evidence or substitute an unrelated sourced amount for it.
function recordActuals(project, actuals, display, rates) {
  if (isBlank(project?.spentToDate)) return { status: "missing", value: null, reason: "No actuals recorded. Remaining requires project-record actuals." };
  const native = num(project.spentToDate);
  const value = native != null && native >= 0 ? convertAmount(native, "USD", display, rates) : null;
  if (value == null) return { status: "unavailable", value: null, issue: native != null && native >= 0 ? "missing-rate" : "invalid-amount", reason: "Recorded actuals are invalid or cannot be converted safely; Remaining is unavailable." };
  const evidence = projectActuals(actuals, display, rates);
  if (evidence.status === "ok" && evidence.value === value) return { ...evidence, value };
  return { status: "ok", value, native, currency: "USD", includesPlan: project.includesPlan === true, provenanceUnavailable: true,
    reason: "Project-record spent-to-date, USD ex tax. Source and as-of cutoff are unavailable or do not match this amount. Category Invoiced is a separate measure." };
}

export function buildHeadlineBar({ budget, forecast, spent }, actualsReason) {
  if (budget == null || forecast == null || budget <= 0 || forecast <= 0) return { status: "unavailable", reason: "A known positive Budget and Forecast are required for the financial composition." };
  if (spent == null) return { status: "unavailable", reason: actualsReason || "Actuals are not recorded, so the forecast cannot be partitioned." };
  if (spent < 0 || spent > forecast) return { status: "unavailable", reason: "Actuals exceed forecast or are negative; the values cannot form a nonnegative partition." };
  const basis = Math.max(budget, forecast);
  const segments = [
    { key: "spent", label: "Actuals spent", value: spent, share: spent / basis },
    { key: "remaining", label: "Remaining forecast", value: forecast - spent, share: (forecast - spent) / basis },
  ];
  if (budget >= forecast) segments.push({ key: "headroom", label: "Under budget", value: budget - forecast, share: (budget - forecast) / basis });
  return { status: "ok", segments, overBudget: forecast > budget ? forecast - budget : null };
}

function projectActuals(actuals, display, rates) {
  if (!actuals?.source?.label || !actuals?.asOfDate) {
    return { status: "missing", value: null, reason: "No sourced actuals are recorded, so the forecast cannot be partitioned into actuals spent, remaining costs and contingency." };
  }
  const native = num(actuals.amount);
  const currency = normCcy(actuals.currency);
  const date = new Date(`${actuals.asOfDate}T00:00:00Z`);
  if (native == null || native < 0 || !currency || !display || !Number.isFinite(date.getTime())) {
    return { status: "unavailable", value: null, reason: "Recorded actuals lack a valid amount, currency or cutoff; actuals-based totals are withheld." };
  }
  const value = convertAmount(native, currency, display, rates);
  const cutoffLabel = `through ${date.toLocaleDateString("en-US", { month: "short", year: "numeric", timeZone: "UTC" })}`;
  return {
    status: value == null ? "unavailable" : "ok", value, native, currency, cutoffLabel,
    issue: value == null ? "missing-rate" : null,
    asOfDate: actuals.asOfDate, source: actuals.source,
    reason: value == null ? "Recorded actuals cannot be converted safely to the display currency; actuals-based totals are withheld." : null,
  };
}

export function buildBar({ totalsAvailable, forecast, spent, actualsReason, contingencyForecast, contingencyRowUnknown }) {
  if (!totalsAvailable) return { status: "unavailable", reason: "Totals are withheld because a row's currency could not be converted safely." };
  if (forecast == null || forecast <= 0) return { status: "unavailable", reason: "No positive forecast total is recorded, so the forecast cannot be partitioned." };
  if (spent == null) return { status: "unavailable", reason: actualsReason || "Actuals are not recorded, so the forecast cannot be partitioned." };
  if (contingencyRowUnknown) return { status: "unavailable", reason: "Contingency forecast is not recorded, so the forecast cannot be partitioned." };
  const remainingCosts = forecast - spent - contingencyForecast;
  if (spent < 0 || contingencyForecast < 0 || remainingCosts < 0) {
    return { status: "unavailable", reason: "Actuals spent plus contingency forecast exceeds the forecast total, so the parts cannot be shown as a partition." };
  }
  return {
    status: "ok",
    segments: [
      { key: "spent", label: "Actuals spent", value: spent, share: spent / forecast },
      { key: "remaining", label: "Remaining costs", value: remainingCosts, share: remainingCosts / forecast },
      { key: "contingency", label: "Contingency forecast", value: contingencyForecast, share: contingencyForecast / forecast },
    ],
  };
}

export const SUB_CENT = 0.005;
export const isZeroMoney = (v) => v != null && Math.abs(v) < SUB_CENT;

/** $34.0M, $2.56M, $524K, $0, -$0.40. Signed adds "+" for positives; negatives use U+2212. */
export function formatCompact(value, currency, { signed = false } = {}) {
  if (value == null || !Number.isFinite(value)) return "—";
  const abs = Math.abs(value);
  const zero = abs < SUB_CENT;
  const sign = zero ? "" : value < 0 ? "−" : signed ? "+" : "";
  const ccy = normCcy(currency);
  const sym = getCurrencySymbol(ccy);
  const prefix = ccy && sym !== ccy ? sym : ccy ? `${ccy} ` : "";
  let body;
  const unit = abs >= 1e9 ? [1e9, "B"] : abs >= 1e6 ? [1e6, "M"] : abs >= 1e3 ? [1e3, "K"] : null;
  if (zero) body = "0";
  else if (!unit) body = Number.isInteger(abs) ? String(abs) : abs.toFixed(2);
  else {
    const scaled = abs / unit[0];
    if (unit[1] === "K") body = (scaled >= 10 ? String(Math.round(scaled)) : scaled.toFixed(1).replace(/\.0$/, "")) + "K";
    else body = (scaled >= 100 ? scaled.toFixed(0) : scaled >= 10 ? scaled.toFixed(1) : scaled.toFixed(2)) + unit[1];
    if (body === "1000K") body = "1.00M";
  }
  return `${sign}${prefix}${body}`;
}

export function formatExact(value, currency) {
  if (value == null || !Number.isFinite(value)) return "Not recorded";
  const sign = value < 0 ? "−" : "";
  return `${sign}${Math.abs(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${normCcy(currency)}`.trim();
}

export function formatPct(r) {
  if (r == null || !Number.isFinite(r)) return "—";
  return `${Math.round(r * 100)}%`;
}
